/**
 * Mixed Arabic/Latin text helpers for the Arabic locale without enabling full RTL layout.
 *
 * Problem: strings like "سحابة QROWNTECH" render with Latin in the wrong order when dir stays LTR.
 * Fix: invisible Unicode bidi isolates steer each script run without flipping the whole page.
 *
 * Used by StorageService.setLabels() and getLocalizedScreenTitle() for menu/API titles.
 */

/** Left-to-Right Isolate — keeps the next run LTR inside Arabic context. */
const LRI = '\u2066';
/** Pop Directional Isolate — closes an LRI/RLI block. */
const PDI = '\u2069';
/** Right-to-Left Isolate — wraps the overall Arabic phrase. */
const RLI = '\u2067';

/**
 * Matches Latin runs to wrap in LRI…PDI:
 * URLs, parenthesized acronyms like (CVC), and word/digit sequences (QROWNTECH, API v2).
 */
const LATIN_RUN =
  /\(https?:\/\/[^)\s\u0600-\u06FF]+\)|https?:\/\/[^\s\u0600-\u06FF]+|\([A-Za-z0-9]+\)|#?[A-Za-z0-9][A-Za-z0-9+#&._\-/®©™%]*(?:\s+[A-Za-z0-9][A-Za-z0-9+#&._\-/®©™%]*)*/g;

const ARABIC_CHAR = /[\u0600-\u06FF]/;
/** Skip re-wrapping when upstream already applied isolates. */
const HAS_BIDI_ISOLATE = /[\u2066\u2067\u2068]/;

/** Rendered at runtime via buildRateLimitMessage — bidi applies to output, not the template. */
const DEFER_BIDI_LABEL_KEYS = new Set([
  'rate_limit_msg_with_time',
  'rate_limit_activation_msg',
  'rate_limit_resend_msg',
  'rate_limit_forgot_credentials_msg',
  'rate_limit_login_msg',
  'button_cooldown_try_again_msg',
  'password_requirements_heading',
  'password_req_length',
  'password_req_uppercase',
  'password_req_lowercase',
  'password_req_number',
  'password_req_special',
]);

/** Bidi wrapping applies only for Arabic UI language. */
function usesMixedLocaleBidi(language: string | null | undefined): boolean {
  return language === 'ar';
}

function resolveUiLanguage(language?: string | null): string | null {
  if (language != null && String(language).trim() !== '') {
    return String(language).trim();
  }
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem('language');
  }
  return null;
}

/** Maps app language code to an Intl locale for number formatting. */
export function getUiNumberLocale(language?: string | null): string {
  const lang = resolveUiLanguage(language);
  if (lang === 'ar') {
    return 'ar-u-nu-arab';
  }
  return lang === 'fr' ? 'fr' : 'en';
}

/** Formats a number using locale-appropriate digits (Arabic-Indic for `ar`). No grouping — years/ids must stay 2026 not 2,026. */
export function formatNumberForLocale(value: number, language?: string | null): string {
  return new Intl.NumberFormat(getUiNumberLocale(language), { useGrouping: false }).format(value);
}

/** Replaces Western digits in a translated label with locale-formatted digits. */
export function localizeWesternDigitsInLabel(
  label: string | null | undefined,
  westernDigits: number,
  language?: string | null
): string {
  if (!label) {
    return '';
  }

  const localizedDigits = formatNumberForLocale(westernDigits, language);
  return label.replace(String(westernDigits), localizedDigits);
}

/** Removes bidi isolate characters so labels can be recomposed safely. */
export function stripBidiIsolates(text: string | null | undefined): string {
  return (text || '').replace(/[\u2066\u2067\u2068\u2069]/g, '');
}

/** e.g. "Total:" + count — Arabic-Indic digits, kept after the label in reading order. */
export function formatLabelWithCount(
  label: string | null | undefined,
  count: number,
  language?: string | null
): string {
  if (!label) {
    return formatNumberForLocale(count, language);
  }

  const localizedCount = formatNumberForLocale(count, language);
  const lang = resolveUiLanguage(language);

  if (lang === 'ar') {
    const cleanLabel = stripBidiIsolates(label).trim();
    return `${RLI}${cleanLabel} ${localizedCount}${PDI}`;
  }

  return `${stripBidiIsolates(label).trim()} ${localizedCount}`;
}

/** Same as formatLabelWithCount but without bidi isolates — safe for Excel/CSV. */
export function formatLabelWithCountPlain(
  label: string | null | undefined,
  count: number,
  language?: string | null
): string {
  if (!label) {
    return formatNumberForLocale(count, language);
  }

  return `${stripBidiIsolates(label).trim()} ${formatNumberForLocale(count, language)}`;
}

/** Wrap each Latin substring so it reads LTR inside an Arabic sentence. */
function isolateLatinRuns(text: string): string {
  return text.replace(LATIN_RUN, (match) => `${LRI}${match}${PDI}`);
}

/**
 * Wraps one UI string for correct mixed-script display in Arabic.
 *
 * No-op when: empty, not Arabic, no Arabic chars, pure Latin, or already isolated.
 * Otherwise: Latin runs get LRI…PDI, then the full string gets RLI…PDI.
 */
export function wrapMixedLocaleBidi(
  text: string | null | undefined,
  language?: string | null
): string {
  if (!text) {
    return '';
  }

  const lang =
    language ??
    (typeof localStorage !== 'undefined' ? localStorage.getItem('language') : null);

  if (!usesMixedLocaleBidi(lang) || !ARABIC_CHAR.test(text) || HAS_BIDI_ISOLATE.test(text)) {
    return text;
  }

  const withLatinIsolates = isolateLatinRuns(text);
  // Pure Arabic (incl. Arabic-Indic digits) — no outer RLI. That wrap moves trailing "."
  // next to digit runs in LTR dialogs (rate-limit wait messages).
  if (withLatinIsolates === text) {
    return text;
  }
  return `${RLI}${withLatinIsolates}${PDI}`;
}

/**
 * Applies wrapMixedLocaleBidi to every string value in a labels map.
 * Called once when translations load (see StorageService.setLabels).
 */
export function wrapMixedLocaleBidiLabels<T extends Record<string, unknown>>(
  labels: T,
  language?: string | null
): T {
  const lang =
    language ??
    (typeof localStorage !== 'undefined' ? localStorage.getItem('language') : null);

  if (!usesMixedLocaleBidi(lang) || !labels) {
    return labels;
  }

  const wrapped = { ...labels } as Record<string, unknown>;
  for (const key of Object.keys(wrapped)) {
    const value = wrapped[key];
    if (typeof value === 'string' && !DEFER_BIDI_LABEL_KEYS.has(key)) {
      wrapped[key] = wrapMixedLocaleBidi(value, lang);
    }
  }

  return wrapped as T;
}

/** Text direction for sidebar/panel blocks that should mirror Arabic reading order. */
export function getUiTextDirection(language: string | null | undefined): 'rtl' | 'ltr' {
  return resolveUiLanguage(language) === 'ar' ? 'rtl' : 'ltr';
}
